import config from '@/config';
import axiosInstance from '@/school-management/utils/api';
import { fetchAllPaginated } from '@/school-management/utils/paginator';
import {
  StudentsPageResponse,
  Student,
  StudentReportItem,
  StudentsReportResponse,
  StudentsReportQueryParams,
} from '@/school-management/types/response';
import { AxiosResponse } from 'axios';

const studentsReportInFlight = new Map<string, Promise<StudentsReportResponse>>();

export interface StudentService {
  getAllStudents: (
    organizationId: string,
    queryParams?: StudentsReportQueryParams
  ) => Promise<StudentsReportResponse>;
  getStudentsByGroup: (organizationId: string, groupId: string) => Promise<Student[]>;
  createStudents: (
    organizationId: string,
    groupId: string,
    users: { userId?: string; firstName: string; lastName: string; email: string }[],
    dealId: string
  ) => Promise<AxiosResponse<any>>;
}

export const studentService: StudentService = {
  async getAllStudents(
    organizationId: string,
    queryParams?: StudentsReportQueryParams
  ): Promise<StudentsReportResponse> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/students/report/search`;

    const body: Record<string, any> = {
      accountId: organizationId,
      page: queryParams?.page || 1,
      pageSize: queryParams?.pageSize || config.studentsPageSize,
      filterByCurrentLevel: !!queryParams?.filterByCurrentLevel,
    };

    // Sorting
    if (queryParams?.sortBy) body.sortBy = queryParams.sortBy;
    if (queryParams?.sortDirection) body.sortDirection = queryParams.sortDirection;

    // Basic text filters
    if (queryParams?.search && queryParams.search.trim()) body.search = queryParams.search.trim();
    if (queryParams?.name && queryParams.name.trim()) body.name = queryParams.name.trim();
    if (queryParams?.teacherName && queryParams.teacherName.trim())
      body.teacherName = queryParams.teacherName.trim();
    if (queryParams?.groupName && queryParams.groupName.trim())
      body.groupName = queryParams.groupName.trim();

    // Basic ID filters
    if (queryParams?.gradeId !== undefined) body.gradeId = queryParams.gradeId;
    if (queryParams?.studentId) body.studentId = queryParams.studentId;
    if (queryParams?.teacherId) body.teacherId = queryParams.teacherId;

    // Array ID filters
    if (queryParams?.gradeIds && queryParams.gradeIds.length > 0)
      body.gradeIds = queryParams.gradeIds;
    if (queryParams?.studentIds && queryParams.studentIds.length > 0)
      body.studentIds = queryParams.studentIds;
    if (queryParams?.teacherIds && queryParams.teacherIds.length > 0)
      body.teacherIds = queryParams.teacherIds;

    if (queryParams?.userStatuses && queryParams.userStatuses.length > 0)
      body.userStatuses = queryParams.userStatuses;

    // Combine single groupId and groupIds array, filter nulls
    const groupIdsToSend = [
      ...(queryParams?.groupId ? [queryParams.groupId] : []),
      ...(queryParams?.groupIds || []),
    ].filter(id => id != null);
    if (groupIdsToSend.length > 0) body.groupIds = groupIdsToSend;

    // Analytics filters - Levels
    if (queryParams?.levels) {
      body.levels = {};
      if (queryParams.levels.starting !== undefined)
        body.levels.starting = queryParams.levels.starting;
      if (queryParams.levels.current !== undefined)
        body.levels.current = queryParams.levels.current;
      if (queryParams.levels.startingList && queryParams.levels.startingList.length > 0)
        body.levels.startingList = queryParams.levels.startingList;
      if (queryParams.levels.currentList && queryParams.levels.currentList.length > 0)
        body.levels.currentList = queryParams.levels.currentList;
      if (Object.keys(body.levels).length === 0) delete body.levels;
    }

    if (queryParams?.levelRanges) {
      body.levelRanges = {};
      if (queryParams.levelRanges.starting) {
        body.levelRanges.starting = {};
        if (queryParams.levelRanges.starting.min !== undefined)
          body.levelRanges.starting.min = queryParams.levelRanges.starting.min;
        if (queryParams.levelRanges.starting.max !== undefined)
          body.levelRanges.starting.max = queryParams.levelRanges.starting.max;
        if (Object.keys(body.levelRanges.starting).length === 0) delete body.levelRanges.starting;
      }
      if (queryParams.levelRanges.current) {
        body.levelRanges.current = {};
        if (queryParams.levelRanges.current.min !== undefined)
          body.levelRanges.current.min = queryParams.levelRanges.current.min;
        if (queryParams.levelRanges.current.max !== undefined)
          body.levelRanges.current.max = queryParams.levelRanges.current.max;
        if (Object.keys(body.levelRanges.current).length === 0) delete body.levelRanges.current;
      }
      if (Object.keys(body.levelRanges).length === 0) delete body.levelRanges;
    }

    // Analytics filters - Activities
    if (queryParams?.activities) {
      body.activities = {};
      if (queryParams.activities.completedExact !== undefined)
        body.activities.completedExact = queryParams.activities.completedExact;
      if (queryParams.activities.completedRange) {
        body.activities.completedRange = {};
        if (queryParams.activities.completedRange.min !== undefined)
          body.activities.completedRange.min = queryParams.activities.completedRange.min;
        if (queryParams.activities.completedRange.max !== undefined)
          body.activities.completedRange.max = queryParams.activities.completedRange.max;
        if (Object.keys(body.activities.completedRange).length === 0)
          delete body.activities.completedRange;
      }
      if (Object.keys(body.activities).length === 0) delete body.activities;
    }

    // Analytics filters - Score
    if (
      typeof queryParams?.score?.from === 'number' ||
      typeof queryParams?.score?.to === 'number'
    ) {
      body.score = {};

      if (typeof queryParams?.score?.from === 'number') {
        body.score.from = queryParams.score.from;
      }

      if (typeof queryParams?.score?.to === 'number') {
        body.score.to = queryParams.score.to;
      }
    }

    // Analytics filters - Time Spent
    if (queryParams?.timeSpent) {
      body.timeSpent = {};
      if (queryParams.timeSpent.secondsExact !== undefined)
        body.timeSpent.secondsExact = queryParams.timeSpent.secondsExact;
      if (queryParams.timeSpent.secondsRange) {
        body.timeSpent.secondsRange = {};
        if (queryParams.timeSpent.secondsRange.min !== undefined)
          body.timeSpent.secondsRange.min = queryParams.timeSpent.secondsRange.min;
        if (queryParams.timeSpent.secondsRange.max !== undefined)
          body.timeSpent.secondsRange.max = queryParams.timeSpent.secondsRange.max;
        if (Object.keys(body.timeSpent.secondsRange).length === 0)
          delete body.timeSpent.secondsRange;
      }
      if (Object.keys(body.timeSpent).length === 0) delete body.timeSpent;
    }

    // Analytics filters - Login Times
    if (queryParams?.loginTimes) {
      body.loginTimes = {};
      if (queryParams.loginTimes.firstLoginExact)
        body.loginTimes.firstLoginExact = queryParams.loginTimes.firstLoginExact;
      if (queryParams.loginTimes.lastLoginExact)
        body.loginTimes.lastLoginExact = queryParams.loginTimes.lastLoginExact;
      if (queryParams.loginTimes.firstLoginRange) {
        body.loginTimes.firstLoginRange = {};
        if (queryParams.loginTimes.firstLoginRange.from)
          body.loginTimes.firstLoginRange.from = queryParams.loginTimes.firstLoginRange.from;
        if (queryParams.loginTimes.firstLoginRange.to)
          body.loginTimes.firstLoginRange.to = queryParams.loginTimes.firstLoginRange.to;
        if (Object.keys(body.loginTimes.firstLoginRange).length === 0)
          delete body.loginTimes.firstLoginRange;
      }
      if (queryParams.loginTimes.lastLoginRange) {
        body.loginTimes.lastLoginRange = {};
        if (queryParams.loginTimes.lastLoginRange.from)
          body.loginTimes.lastLoginRange.from = queryParams.loginTimes.lastLoginRange.from;
        if (queryParams.loginTimes.lastLoginRange.to)
          body.loginTimes.lastLoginRange.to = queryParams.loginTimes.lastLoginRange.to;
        if (Object.keys(body.loginTimes.lastLoginRange).length === 0)
          delete body.loginTimes.lastLoginRange;
      }
      if (Object.keys(body.loginTimes).length === 0) delete body.loginTimes;
    }

    // Analytics filters - hasActivities
    if (typeof queryParams?.hasActivities === 'boolean') {
      body.hasActivities = queryParams.hasActivities;
    }

    const requestKey = `${url}:${JSON.stringify(body)}`;
    const existing = studentsReportInFlight.get(requestKey);
    if (existing) return existing;

    const promise = axiosInstance
      .post<StudentsReportResponse>(url, body)
      .then(res => res.data)
      .finally(() => {
        studentsReportInFlight.delete(requestKey);
      });
    studentsReportInFlight.set(requestKey, promise);
    return promise;
  },
  async getStudentsByGroup(organizationId: string, groupId: string): Promise<Student[]> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/students/group/${groupId}`;

    const fetchPage = async (pageInfo?: string): Promise<StudentsPageResponse> => {
      const params = pageInfo ? { cursor: pageInfo } : undefined;
      const { data } = await axiosInstance.get<StudentsPageResponse>(url, { params });
      return data;
    };

    const getItems = (resp: StudentsPageResponse) => resp.data?.students ?? [];
    const getNextKey = (resp: StudentsPageResponse) => resp.data?.pageInfo;

    return fetchAllPaginated<StudentsPageResponse, Student>(fetchPage, getItems, getNextKey);
  },
  async createStudents(
    organizationId: string,
    groupId: string,
    users: { userId?: string; firstName: string; lastName: string; email: string }[],
    dealId: string
  ): Promise<AxiosResponse<any>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/students/createStudents`;
    return axiosInstance.post(url, {
      organizationId,
      groupId,
      users,
      dealId,
    });
  },
};

/** One page of students report for `groupId` (class size is capped at `maxStudentsPerClass`). */
export async function fetchAllStudentsReportItemsForGroup(
  organizationId: string,
  groupId: string
): Promise<StudentReportItem[]> {
  const res = await studentService.getAllStudents(organizationId, {
    page: 1,
    pageSize: config.maxStudentsPerClass,
    groupIds: [groupId],
    sortBy: 'name',
    sortDirection: 'asc',
  });
  return res.data.items;
}
